import type React from 'react';
import { PureComponent, type ReactNode } from 'react';

import Icon, { ICONS } from '../../../icons/icon';
import Button from '../../../ui/button/button';
import FieldText from '../../../ui/input_field/input_field';
import type { ItemComponentProps } from '../../sortable_list/sortable_list';

import styles from './key_value_item.module.css';

export type StateValue = { label: string; value: string; id: string };

export type KeyValueItemProps = ItemComponentProps<StateValue> & {
  autofillValue?: boolean;
  fieldName: string;
  labelPlaceholder?: string;
  movable: boolean;
  onChange: (id: string, key: string, value: string) => void;
  onDeleteClick?: (id: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  valuePlaceholder?: string;
  disabled?: boolean;
};

type State = {
  key: string;
  value: string;
  valueUntouched: boolean;
};

class KeyValueItem extends PureComponent<KeyValueItemProps, State> {
  constructor(props: KeyValueItemProps) {
    super(props);
    const { label, value } = props.item;
    this.state = {
      key: label || '',
      value: value || '',
      valueUntouched: !value && !label,
    };
  }

  fireOnChange = (): void => {
    const { key, value } = this.state;
    this.props.onChange(this.props.item.id, key, value);
  };

  handleKeyChange = (inputValue: string | undefined): void => {
    if (inputValue === undefined) {
      return;
    }
    const newState = {
      key: inputValue,
      value: this.state.value ?? '',
    };

    if (this.state.valueUntouched && this.props.autofillValue) {
      newState.value = inputValue;
    }

    this.setState(newState, this.fireOnChange);
  };

  handleValueChange = (value: string | undefined): void => {
    if (value === undefined) {
      return;
    }
    this.setState(
      {
        value,
        valueUntouched: false,
      },
      this.fireOnChange,
    );
  };

  render(): ReactNode {
    const { key, value } = this.state;
    const {
      item,
      onDeleteClick,
      movable,
      labelPlaceholder,
      valuePlaceholder,
      fieldName,
    } = this.props;

    return (
      <div className={styles.root_item}>
        {movable ? (
          <div className={styles.drag_icon}>
            <Icon icon={ICONS.Hamburger} size={16} />
          </div>
        ) : undefined}
        <div className={styles.key}>
          <FieldText
            autoFocus={!item.label && !item.value}
            name={`${fieldName}_${item.id}_label`}
            value={key}
            placeholder={labelPlaceholder ?? 'Label'}
            onChange={this.handleKeyChange}
            onKeyDown={this.props.onKeyDown}
            disabled={this.props.disabled}
          />
        </div>
        <div className={styles.value}>
          <FieldText
            name={`${fieldName}_${item.id}_value`}
            value={value}
            placeholder={valuePlaceholder ?? 'Value'}
            onChange={this.handleValueChange}
            onKeyDown={this.props.onKeyDown}
            disabled={this.props.disabled}
          />
        </div>
        {onDeleteClick && (
          <Button
            className={styles.delete_button}
            onClick={() => onDeleteClick(item.id)}
            icon={ICONS.CrossMark}
            noBorder
            variant="danger"
            size="s"
            disabled={this.props.disabled}
          />
        )}
      </div>
    );
  }
}

export default KeyValueItem;
